import { FC, useEffect, useState } from 'react'
import cn from 'classnames'
import DOMPurify from 'dompurify'

import useMessenger from '@/hooks/useMessenger'
import useDebounce from '@/hooks/useDebounce'

import ChatBubble from '../../ChatBubble'
import InputSelect from '../../InputSelect'

import { MESSAGE_TYPES } from '@/context/constants'

import { IAction, IMessage, StringObject } from '@/context/types'
import { useQuery } from '@apollo/client'
import { getProperties } from '@/common/graphql/getProperties'
import { getPropertyUnitsQuery } from '@/common/graphql/getPropertyUnits'
import { sortDisplayNames } from '../utils'

interface TicketFormProps {
  message: IMessage
  direction: 'left' | 'right'
}

interface TicketForm {
  ticketDescription: string
  propertyID: any | null
  unit: any | null
}

const MAX_ROWS = 5
const MIN_ROWS = 3

const TicketForm: FC<TicketFormProps> = ({ message, direction }) => {
  const [ticketForm, setTicketForm] = useState<TicketForm>({
    ticketDescription: DOMPurify.sanitize(
      message.metadata?.ticketDescription || '',
      {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
      },
    ),
    propertyID: '',
    unit: '',
  })
  const { loading, data } = useQuery(getProperties, {
    fetchPolicy: 'cache-and-network',
  })
  const { loading: unitLoading, data: unitData } = useQuery(
    getPropertyUnitsQuery,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        propertyID: ticketForm.propertyID,
      },
      skip: !ticketForm.propertyID,
    },
  )
  const [error, setError] = useState({
    description: undefined,
    property: undefined,
    unit: undefined,
  })
  const [textAreaRows, setTextAreaRows] = useState(MIN_ROWS)
  const [isMultiUnit, setIsMultiUnit] = useState(false)
  const [propertyOptions, setPropertyOptions] = useState<any>([])
  const [unitOptions, setUnitOptions] = useState<any>([])

  const {
    getSelectedConversation,
    getConfig,
    sendMessage,
    isConversationNew,
    getLastConversationMessage,
  } = useMessenger()

  const lastMessage = getLastConversationMessage()

  const { myProperties } = data || { myProperties: [] }
  const { getPropertyUnits } = unitData || { getPropertyUnits: [] }

  const handleOnClick = (action: IAction, metadata: StringObject) => {
    const { text, payload } = action

    const sendPayload = {
      type: MESSAGE_TYPES.MESSAGE,
      conversationId: getSelectedConversation(),
      author: {
        type: getConfig()?.userType,
        userExternalId: getConfig()?.userExternalId,
      },
      content: {
        type: 'text',
        text,
        payload,
      },
      metadata: {
        ...metadata,
        source: 'poplar-messenger',
      },
    }

    sendMessage(sendPayload)
  }

  useEffect(() => {
    if (ticketForm.propertyID && myProperties?.length > 0) {
      const selectedProperty = myProperties.find(
        (property: any) => property.propertyID === ticketForm.propertyID,
      )

      if (selectedProperty?.unitType === 'Multi Unit') {
        setIsMultiUnit(true)
      } else {
        setIsMultiUnit(false)
        setTicketForm(prev => ({ ...prev, unit: null }))
      }
    } else {
      setIsMultiUnit(false)
      setTicketForm(prev => ({ ...prev, unit: null }))
    }
  }, [ticketForm.propertyID])

  useEffect(() => {
    if (myProperties?.length > 0) {
      if (myProperties?.length === 1) {
        handleSelectChange(myProperties[0].propertyID, 'propertyID')
      }

      const propertyList = sortDisplayNames(
        myProperties?.map((property: any) => ({
          text: property.propertyDisplayName,
          value: property.propertyID,
        })),
      )

      setPropertyOptions(propertyList)
    }
  }, [myProperties])

  useEffect(() => {
    if (getPropertyUnits?.length > 0) {
      if (getPropertyUnits?.length === 1) {
        handleSelectChange(getPropertyUnits[0].propertyID, 'unit')
      }

      const unitList = sortDisplayNames(
        getPropertyUnits?.map((unit: any) => ({
          text: unit.unitNumber,
          value: unit.propertyID,
        })),
      )

      setUnitOptions(unitList)
    }
  }, [getPropertyUnits])

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const height = event.target.scrollHeight
    const rowHeight = 24
    const tRows = Math.ceil(height / rowHeight) - 1

    if (tRows) {
      setTextAreaRows(tRows > MAX_ROWS ? MAX_ROWS : tRows)
    }

    setTicketForm(prev => ({ ...prev, ticketDescription: event.target.value }))
  }

  const handleSelectChange = (value: any, key: string) => {
    setTicketForm(prev => ({ ...prev, [key]: value }))
  }

  return (
    <>
      {/* {message.content?.text && (
        <ChatBubble direction={direction}>
          <pre className="sm:pmui-text-xs md:pmui-text-sm pmui-font-eina03 pmui-whitespace-pre-wrap">
            <div dangerouslySetInnerHTML={{ __html: message.content?.text }} />
          </pre>
        </ChatBubble>
      )} */}
      <ChatBubble direction={direction} isForm>
        <div>
          <div className="pmui-flex pmui-flex-col pmui-gap-3 pmui-p-2">
            <div>
              <div
                className={cn(
                  'pmui-text-sm pmui-font-semibold',
                  { 'pmui-text-pop-gray-400': !error.description },
                  { 'pmui-text-red-500': error.description },
                )}
              >
                Tell us about your concern
                <span className="pmui-text-red-500 pmui-text-sm"> *</span>
              </div>
              <textarea
                rows={ticketForm.ticketDescription ? textAreaRows : MIN_ROWS}
                value={ticketForm.ticketDescription}
                onChange={handleChange}
                className={cn(
                  'pmui-text-xs pmui-font-semibold pmui-font-eina03 pmui-border-none \
                   pmui-mb-0 pmui-pb-4 pmui-resize-none pmui-w-full  md:pmui-px-5 pmui-py-4',
                )}
                placeholder="Type your concern here"
                disabled={
                  !isConversationNew ||
                  lastMessage?.content?.type !== 'ticket_form'
                }
              />
            </div>
            {myProperties?.length > 1 && (
              <div className="pmui-w-full">
                <InputSelect
                  label="Select your property"
                  labelStyle="text-pop-gray-200"
                  onChange={(value: string) => {
                    handleSelectChange(value, 'propertyID')
                  }}
                  options={[
                    { text: 'All properties', value: '' },
                    ...propertyOptions,
                  ]}
                  value={ticketForm.propertyID}
                  error={error.property}
                  className="w-full"
                  isRequired
                  disabled={
                    loading ||
                    !isConversationNew ||
                    lastMessage?.content?.type !== 'ticket_form'
                  }
                  isLoading={loading}
                />
              </div>
            )}

            {isMultiUnit && (
              <div className="pmui-w-full">
                <InputSelect
                  label="Select your unit"
                  labelStyle="text-pop-gray-200"
                  onChange={(value: string) => {
                    handleSelectChange(value, 'unit')
                  }}
                  options={[{ text: 'All units', value: '' }, ...unitOptions]}
                  value={ticketForm.unit}
                  error={error.unit}
                  className="w-full"
                  isRequired
                  disabled={
                    unitLoading ||
                    !isConversationNew ||
                    lastMessage?.content?.type !== 'ticket_form'
                  }
                  isLoading={unitLoading}
                />
              </div>
            )}
            <button
              className={cn('pmui-btn btn-secondary pmui-w-full btn-primary')}
              onClick={useDebounce(() => {
                const action = {
                  type: 'postback',
                  text: DOMPurify.sanitize(ticketForm.ticketDescription, {
                    ALLOWED_TAGS: [],
                    ALLOWED_ATTR: [],
                  }),
                  payload: 'YES_CREATE_TICKET',
                }
                handleOnClick(action, {
                  ...ticketForm,
                  ticketDescription: DOMPurify.sanitize(
                    ticketForm.ticketDescription || '',
                    {
                      ALLOWED_TAGS: [],
                      ALLOWED_ATTR: [],
                    },
                  ),
                  propertyID: ticketForm.propertyID || null,
                  unit: ticketForm.unit || null,
                })
              }, getConfig()?.debounceTimeout)}
              disabled={
                loading ||
                !isConversationNew ||
                lastMessage?.content?.type !== 'ticket_form' ||
                !ticketForm.ticketDescription ||
                (myProperties?.length > 0 && !ticketForm.propertyID)
              }
            >
              Create case
            </button>
            <button
              className={cn('pmui-btn btn-secondary pmui-w-full')}
              onClick={useDebounce(() => {
                const action = {
                  type: 'postback',
                  text: 'No, I am good',
                  payload: 'NO_CREATE_TICKET',
                }
                handleOnClick(action, {})
              }, getConfig()?.debounceTimeout)}
              disabled={
                !isConversationNew ||
                lastMessage?.content?.type !== 'ticket_form'
              }
            >
              No, I am good
            </button>
          </div>
        </div>
      </ChatBubble>
    </>
  )
}

export default TicketForm
